/**
 * EVENTSFLO-CLIENT-PORTAL-PHASE-2-PROJECT-SHIFT-REQUESTS-V1
 * Client Project Request — New submission form.
 *
 * All client_users (admin or viewer) can submit. Server enforces scoping;
 * the form never sends client_id or company_id.
 */
import { useState, useEffect, useMemo } from "react";
import {
  normaliseTime,
  diffHours,
  addHours,
  expandDateRange,
  formatDurationHM,
} from "./timeShiftHelpers";
import { useOutletContext, useNavigate, useParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Separator } from "../../components/ui/separator";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  Loader2,
  ChevronLeft,
  Info,
  Calendar,
  Users,
} from "lucide-react";
import { ShiftRequirementsEditor } from "../../components/client/ShiftRequirementsEditor";
// EVENTSFLO-GUIDE-BUTTON-ROLLOUT-V1
import { GuideButton } from "../../components/common/GuideButton";
import { useI18n } from "../../i18n/I18nProvider";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const emptyShift = () => ({
  date: "",
  start_time: "",
  end_time: "",
  staff_required: 1,
  role: "",
  activity: "",
  location: "",
  notes: "",
  // EVENTSFLO-CLIENT-PORTAL-SHIFT-REQUIREMENTS-LINE-ITEMS-V1
  requirements: [],
  // EVENTSFLO-CLIENT-PORTAL-PROJECT-REQUEST-VENUE-LOCATION-DROPDOWNS-V1
  venue_id: "",
  venue_location_id: "",
  _venue_manual: false,    // user picked a venue different from project
  _loc_manual: false,      // user picked a loc different from project
  // EVENTSFLO-CLIENT-PORTAL-PROJECT-REQUEST-SHIFT-TIME-UX-V1
  // duration as a string for the controlled input ("" allowed); the
  // numeric value is derived at submit time. last-edited-wins logic
  // means: editing duration recomputes end_time; editing end_time
  // recomputes duration.
  duration_hours_str: "",
  _start_error: "",
  _end_error: "",
  _duration_error: "",
});

export default function ClientProjectRequestNewPage() {
  const { getToken } = useOutletContext();
  const navigate = useNavigate();
  const { t } = useI18n();
  // EVENTSFLO-CLIENT-PORTAL-PROJECT-REQUEST-EDIT-DELETE-V1
  // Same component handles both create (`/client/project-requests/new`)
  // and edit (`/client/project-requests/:request_id/edit`).
  const { request_id: editRequestId } = useParams();
  const isEdit = Boolean(editRequestId);

  const [submitting, setSubmitting] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(isEdit);
  const [form, setForm] = useState({
    project_name: "",
    event_type: "",
    venue: "",
    venue_address: "",
    meeting_point: "",
    // EVENTSFLO-CLIENT-PORTAL-PROJECT-REQUEST-VENUE-LOCATION-DROPDOWNS-V1
    requested_venue_id: "",
    requested_venue_location_id: "",
    start_date: "",
    end_date: "",
    notes: "",
    on_site_contact: { name: "", phone: "", email: "" },
  });
  const [shifts, setShifts] = useState([emptyShift()]);

  // EVENTSFLO-CLIENT-PORTAL-PROJECT-REQUEST-VENUE-LOCATION-DROPDOWNS-V1
  // Client venue catalogue. Loaded once; if exactly one venue exists,
  // default at project level. If >1, default to the first sorted.
  const [venues, setVenues] = useState([]);
  const [venuesLoaded, setVenuesLoaded] = useState(false);
  const [venuesError, setVenuesError] = useState(null);

  // EVENTSFLO-CLIENT-PORTAL-REQUEST-JOB-UX-COMPACT-SHIFT-DUPLICATE-CONTACTS-V1
  // Saved client contacts for the on-site contact picker. Loaded once.
  // If none exist (or the fetch fails), the form silently falls back
  // to manual entry — no toast, no blocking error.
  const [savedContacts, setSavedContacts] = useState([]);
  const [contactsLoaded, setContactsLoaded] = useState(false);
  const [oscMode, setOscMode] = useState("manual"); // "manual" | "saved"
  const [oscSelectedId, setOscSelectedId] = useState("");

  useEffect(() => {
    const token = getToken();
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/client/contacts`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (cancelled) return;
        const list = Array.isArray(data.items) ? data.items : [];
        setSavedContacts(list);
        setContactsLoaded(true);
      } catch (e) {
        if (!cancelled) {
          // Graceful fallback — keep `manual` mode and let the user type.
          setSavedContacts([]);
          setContactsLoaded(true);
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const token = getToken();
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/client/venues`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (cancelled) return;
        const list = Array.isArray(data.venues) ? data.venues : [];
        setVenues(list);
        setVenuesLoaded(true);
        if (list.length >= 1) {
          // Default to first sorted venue.
          const first = list[0];
          const firstLoc = (first.locations || [])[0];
          setForm((p) => ({
            ...p,
            requested_venue_id: first.id,
            venue: first.name || p.venue,
            venue_address: first.address || p.venue_address,
            requested_venue_location_id: firstLoc ? firstLoc.id : "",
            meeting_point: firstLoc?.name || p.meeting_point,
          }));
          setShifts((arr) =>
            arr.map((s) => ({
              ...s,
              venue_id: s._venue_manual ? s.venue_id : first.id,
              venue_location_id: s._loc_manual
                ? s.venue_location_id
                : firstLoc?.id || "",
            }))
          );
        }
      } catch (e) {
        if (!cancelled) {
          setVenuesError(e.message);
          setVenuesLoaded(true);
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // EVENTSFLO-CLIENT-PORTAL-PROJECT-REQUEST-EDIT-DELETE-V1
  // When in edit mode, hydrate the form from the existing request.
  // Runs once after the venues effect has set defaults; the existing
  // request overrides those defaults.
  useEffect(() => {
    if (!isEdit) return;
    const token = getToken();
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `${API_URL}/api/client/project-requests/${editRequestId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (cancelled) return;
        if (data.status && !["submitted", "under_review"].includes(data.status)) {
          toast.error(
            "This request can no longer be edited."
          );
          navigate("/client/project-requests");
          return;
        }
        setForm({
          project_name: data.project_name || "",
          event_type: data.event_type || "",
          venue: data.venue || "",
          venue_address: data.venue_address || "",
          meeting_point: data.meeting_point || "",
          requested_venue_id: data.requested_venue_id || "",
          requested_venue_location_id: data.requested_venue_location_id || "",
          start_date: data.start_date || "",
          end_date: data.end_date || "",
          notes: data.notes || "",
          on_site_contact: data.on_site_contact || {
            name: "", phone: "", email: "",
          },
        });
        setShifts(
          (data.shifts || [emptyShift()]).map((s) => ({
            ...emptyShift(),
            ...s,
            duration_hours_str:
              s.duration_hours !== null && s.duration_hours !== undefined
                ? String(s.duration_hours)
                : "",
          })),
        );
      } catch (e) {
        if (!cancelled) {
          toast.error(t("clientPortal:projectRequestNew.failedToLoadEdit"));
        }
      } finally {
        if (!cancelled) setLoadingExisting(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, editRequestId]);

  const venuesById = useMemo(() => {
    const m = {};
    for (const v of venues) m[v.id] = v;
    return m;
  }, [venues]);

  const locationsForVenue = (vid) =>
    venuesById[vid]?.locations || [];

  // When project-level venue changes, cascade to untouched shifts.
  const handleProjectVenueChange = (newVenueId) => {
    const v = venuesById[newVenueId];
    const firstLoc = (v?.locations || [])[0];
    setForm((p) => ({
      ...p,
      requested_venue_id: newVenueId,
      venue: v?.name || p.venue,
      venue_address: v?.address || p.venue_address,
      requested_venue_location_id: firstLoc ? firstLoc.id : "",
      meeting_point: firstLoc?.name || p.meeting_point,
    }));
    setShifts((arr) =>
      arr.map((s) =>
        s._venue_manual
          ? s
          : {
              ...s,
              venue_id: newVenueId,
              venue_location_id: s._loc_manual
                ? s.venue_location_id
                : firstLoc?.id || "",
            }
      )
    );
  };

  const handleProjectLocationChange = (newLocId) => {
    const loc = locationsForVenue(form.requested_venue_id).find(
      (l) => l.id === newLocId
    );
    setForm((p) => ({
      ...p,
      requested_venue_location_id: newLocId,
      meeting_point: loc?.name || p.meeting_point,
    }));
    setShifts((arr) =>
      arr.map((s) =>
        s._loc_manual || s.venue_id !== form.requested_venue_id
          ? s
          : { ...s, venue_location_id: newLocId }
      )
    );
  };

  const handleShiftVenueChange = (i, vid) => {
    const v = venuesById[vid];
    const firstLoc = (v?.locations || [])[0];
    const manual = vid !== form.requested_venue_id;
    setShifts((arr) =>
      arr.map((s, idx) =>
        idx === i
          ? {
              ...s,
              venue_id: vid,
              venue_location_id: firstLoc ? firstLoc.id : "",
              _venue_manual: manual,
              _loc_manual: false,
            }
          : s
      )
    );
  };

  const handleShiftLocationChange = (i, lid) => {
    setShifts((arr) =>
      arr.map((s, idx) =>
        idx === i
          ? {
              ...s,
              venue_location_id: lid,
              _loc_manual: lid !== form.requested_venue_location_id,
            }
          : s
      )
    );
  };

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setOsc = (k, v) =>
    setForm((p) => ({
      ...p,
      on_site_contact: { ...p.on_site_contact, [k]: v },
    }));

  // EVENTSFLO-CLIENT-PORTAL-REQUEST-JOB-UX-COMPACT-SHIFT-DUPLICATE-CONTACTS-V1
  // Apply a saved contact to the on-site contact form. Stores the
  // selected contact id alongside the snapshot fields so the server
  // can validate `client_contact_id` ownership (see
  // server.py:38353). Clearing the selection (passing "") wipes the
  // id but leaves the snapshot in place — user can keep editing.
  const handleSavedContactPick = (contactId) => {
    setOscSelectedId(contactId);
    if (!contactId) {
      setForm((p) => ({
        ...p,
        on_site_contact: {
          ...p.on_site_contact,
          client_contact_id: undefined,
        },
      }));
      return;
    }
    const picked = savedContacts.find((c) => c.id === contactId);
    if (!picked) return;
    setForm((p) => ({
      ...p,
      on_site_contact: {
        client_contact_id: picked.id,
        name: picked.name || "",
        phone: picked.phone || "",
        email: picked.email || "",
      },
    }));
  };

  const updateShift = (i, k, v) =>
    setShifts((arr) =>
      arr.map((s, idx) => (idx === i ? { ...s, [k]: v } : s))
    );

  // EVENTSFLO-CLIENT-PORTAL-PROJECT-REQUEST-SHIFT-TIME-UX-V1
  // onBlur normalisers. Empty input is allowed at blur (caller decides
  // whether to error on submit). Invalid → keep the raw text and set
  // an inline error. Recomputes duration when both times are present.
  const handleStartTimeBlur = (i) => {
    setShifts((arr) =>
      arr.map((s, idx) => {
        if (idx !== i) return s;
        const raw = s.start_time;
        if (!raw || raw === "") {
          return { ...s, _start_error: "" };
        }
        const norm = normaliseTime(raw);
        if (norm === null) {
          return { ...s, _start_error: "Invalid time (use HH:MM, e.g. 08:30)" };
        }
        // If end_time is set + valid, refresh duration from start+end.
        let nextDuration = s.duration_hours_str;
        let durErr = "";
        if (s.end_time) {
          const h = diffHours(norm, s.end_time);
          nextDuration = h !== null ? String(h) : s.duration_hours_str;
          if (h === null) {
            durErr = "End time must be after start";
          }
        }
        return {
          ...s,
          start_time: norm,
          _start_error: "",
          duration_hours_str: nextDuration,
          _duration_error: durErr,
        };
      })
    );
  };

  const handleEndTimeBlur = (i) => {
    setShifts((arr) =>
      arr.map((s, idx) => {
        if (idx !== i) return s;
        const raw = s.end_time;
        if (!raw || raw === "") {
          return { ...s, _end_error: "" };
        }
        const norm = normaliseTime(raw);
        if (norm === null) {
          return { ...s, _end_error: "Invalid time (use HH:MM, e.g. 17:30)" };
        }
        // Last-edited-wins: editing end_time recomputes duration.
        let nextDuration = s.duration_hours_str;
        let durErr = "";
        if (s.start_time) {
          const h = diffHours(s.start_time, norm);
          if (h === null) {
            durErr = "End time must be after start";
          } else {
            nextDuration = String(h);
          }
        }
        return {
          ...s,
          end_time: norm,
          _end_error: "",
          duration_hours_str: nextDuration,
          _duration_error: durErr,
        };
      })
    );
  };

  const handleDurationBlur = (i) => {
    setShifts((arr) =>
      arr.map((s, idx) => {
        if (idx !== i) return s;
        const raw = (s.duration_hours_str || "").toString().trim();
        if (!raw) {
          return { ...s, _duration_error: "" };
        }
        const d = parseFloat(raw);
        if (!Number.isFinite(d) || d <= 0 || d >= 24) {
          return { ...s, _duration_error: "Invalid duration (hours, e.g. 8 or 9.5)" };
        }
        // Last-edited-wins: editing duration recomputes end_time from start.
        let nextEnd = s.end_time;
        let endErr = "";
        if (s.start_time) {
          const e = addHours(s.start_time, d);
          if (e === null) {
            endErr = "Duration overflows day from start time";
          } else {
            nextEnd = e;
          }
        }
        return {
          ...s,
          duration_hours_str: String(d),
          _duration_error: "",
          end_time: nextEnd,
          _end_error: endErr,
        };
      })
    );
  };
  // EVENTSFLO-CLIENT-PORTAL-REQUEST-JOB-UX-COMPACT-SHIFT-DUPLICATE-CONTACTS-V1
  // Add a new shift. For the FIRST shift (none exist yet) we use the
  // current blank initial template. For every subsequent shift we
  // CLONE the previous shift's details (deep-copy so the source row
  // is never mutated by reference) and bump the date by +1 calendar
  // day. If the previous date is missing/invalid we leave the new
  // date blank rather than guessing. Notes-style transient error
  // fields (`_start_error` / `_end_error` / `_duration_error`) are
  // reset on the clone so we don't carry over stale validation state.
  const addShift = () =>
    setShifts((arr) => {
      if (arr.length === 0) {
        return [
          {
            ...emptyShift(),
            venue_id: form.requested_venue_id || "",
            venue_location_id: form.requested_venue_location_id || "",
          },
        ];
      }
      const prev = arr[arr.length - 1];
      // Increment date by +1 day if previous date is a valid YYYY-MM-DD.
      let nextDate = "";
      if (prev.date && /^\d{4}-\d{2}-\d{2}$/.test(prev.date)) {
        const d = new Date(`${prev.date}T00:00:00Z`);
        if (!Number.isNaN(d.getTime())) {
          d.setUTCDate(d.getUTCDate() + 1);
          const y = d.getUTCFullYear();
          const m = String(d.getUTCMonth() + 1).padStart(2, "0");
          const day = String(d.getUTCDate()).padStart(2, "0");
          nextDate = `${y}-${m}-${day}`;
        }
      }
      const cloned = {
        ...prev,
        // Deep-clone requirements so each row owns its own list.
        requirements: Array.isArray(prev.requirements)
          ? prev.requirements.map((r) => ({ ...r }))
          : [],
        date: nextDate,
        _start_error: "",
        _end_error: "",
        _duration_error: "",
      };
      return [...arr, cloned];
    });
  // EVENTSFLO-CLIENT-PORTAL-REQUEST-JOB-SHIFT-CARD-INLINE-COMPACT-V1
  // Copy a fixed list of non-empty fields FROM the source shift onto
  // every OTHER shift in the list. Never copies `date` (each shift
  // owns its own date) and never copies fields whose source value is
  // empty/blank — so a blank field won't accidentally wipe existing
  // values on the other rows. Returns the count of shifts that were
  // actually mutated so the caller can show a contextual toast.
  const applyShiftFieldsToAll = (sourceIdx, fieldNames) => {
    setShifts((arr) => {
      if (sourceIdx < 0 || sourceIdx >= arr.length) return arr;
      const src = arr[sourceIdx];
      // Build the payload of non-empty source values only.
      const payload = {};
      for (const fn of fieldNames) {
        const v = src?.[fn];
        if (v !== undefined && v !== null && String(v).trim() !== "") {
          payload[fn] = v;
        }
      }
      if (Object.keys(payload).length === 0) return arr;
      return arr.map((row, idx) => {
        if (idx === sourceIdx) return row;
        // Deep-safe copy — never mutate the source by reference.
        return { ...row, ...payload };
      });
    });
  };

  const applyTimesToAll = (i) => {
    if (shifts.length < 2) {
      toast("Add another shift first to use Apply to all.");
      return;
    }
    applyShiftFieldsToAll(i, ["start_time", "end_time", "duration_hours_str"]);
    toast.success(`Times copied to ${shifts.length - 1} other shift(s).`);
  };
  const applyStaffToAll = (i) => {
    if (shifts.length < 2) {
      toast("Add another shift first to use Apply to all.");
      return;
    }
    applyShiftFieldsToAll(i, ["staff_required"]);
    toast.success(`Staff count copied to ${shifts.length - 1} other shift(s).`);
  };
  const applyRoleToAll = (i) => {
    if (shifts.length < 2) {
      toast("Add another shift first to use Apply to all.");
      return;
    }
    applyShiftFieldsToAll(i, ["role"]);
    toast.success(`Role copied to ${shifts.length - 1} other shift(s).`);
  };
  const applyActivityToAll = (i) => {
    if (shifts.length < 2) {
      toast("Add another shift first to use Apply to all.");
      return;
    }
    applyShiftFieldsToAll(i, ["activity"]);
    toast.success(`Activity copied to ${shifts.length - 1} other shift(s).`);
  };

  const removeShift = (i) =>
    setShifts((arr) => arr.filter((_, idx) => idx !== i));

  // EVENTSFLO-CLIENT-PORTAL-PROJECT-REQUEST-SHIFT-TIME-UX-V1
  // Generate one shift row per date in [start_date, end_date]. Uses
  // the FIRST shift in the list as a template (its role/activity/
  // venue/staff/times). Existing rows are kept; newly generated rows
  // are appended as fresh editable entries. The user can delete any
  // of them before submitting.
  const handleGenerateDailyShifts = () => {
    if (!form.start_date || !form.end_date) {
      toast.error(t("clientPortal:projectRequestNew.setStartEndDates"));
      return;
    }
    const dates = expandDateRange(form.start_date, form.end_date, { max: 31 });
    if (dates.length === 0) {
      toast.error(t("clientPortal:projectRequestNew.endDateMustBeAfter"));
      return;
    }
    if (dates.length > 31) {
      toast.error("Range too long (max 31 days).");
      return;
    }
    const template = shifts[0] || emptyShift();
    const baseStart =
      normaliseTime(template.start_time || "") || template.start_time || "";
    const baseEnd =
      normaliseTime(template.end_time || "") || template.end_time || "";
    const dur =
      template.duration_hours_str ||
      (baseStart && baseEnd
        ? String(diffHours(baseStart, baseEnd) ?? "")
        : "");

    const generated = dates.map((d) => ({
      ...emptyShift(),
      date: d,
      start_time: baseStart,
      end_time: baseEnd,
      duration_hours_str: dur,
      staff_required: template.staff_required || 1,
      role: template.role || "",
      activity: template.activity || "",
      location: template.location || "",
      venue_id: template.venue_id || form.requested_venue_id || "",
      venue_location_id:
        template.venue_location_id || form.requested_venue_location_id || "",
      _venue_manual: template._venue_manual || false,
      _loc_manual: template._loc_manual || false,
      // EVENTSFLO-CLIENT-PORTAL-SHIFT-REQUIREMENTS-LINE-ITEMS-V1
      // Deep-clone requirements so each generated day owns its own list
      // (later edits to one day don't leak into others).
      requirements: Array.isArray(template.requirements)
        ? template.requirements.map((r) => ({ ...r }))
        : [],
    }));
    setShifts(generated);
    toast.success(`Generated ${generated.length} shift row(s). Review and edit before submit.`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // EVENTSFLO-CLIENT-REQUEST-SUBMISSION-BODY-STREAM-READ-FIX-V1:
    // Hard idempotency guard. The form's `onSubmit` AND the Submit button's
    // implicit click both go through here; if the user double-clicks (or
    // presses Enter) before React flushes `disabled={submitting}` we would
    // otherwise fire two parallel fetches.
    if (submitting) return;
    if (!form.project_name.trim()) {
      toast.error(t("clientPortal:projectRequestNew.projectNameRequired"));
      return;
    }
    if (shifts.length === 0) {
      toast.error(t("clientPortal:projectRequestNew.oneShiftRequired"));
      return;
    }
    // EVENTSFLO-CLIENT-PORTAL-PROJECT-REQUEST-SHIFT-TIME-UX-V1
    // Final pass: normalise + validate each row's times. If any row
    // has an unresolved time error or missing values, abort submit.
    const normalised = shifts.map((s) => {
      const st = s.start_time ? normaliseTime(s.start_time) : "";
      const et = s.end_time ? normaliseTime(s.end_time) : "";
      let dur = null;
      if (s.duration_hours_str) {
        const d = parseFloat(s.duration_hours_str);
        if (Number.isFinite(d) && d > 0 && d < 24) dur = d;
      }
      if (dur === null && st && et) {
        dur = diffHours(st, et);
      }
      return { ...s, start_time: st || "", end_time: et || "", _duration_value: dur };
    });
    for (let i = 0; i < normalised.length; i++) {
      const s = normalised[i];
      if (!s.date) {
        toast.error(t("clientPortal:projectRequestNew.shiftDateRequired", { n: i + 1 }));
        return;
      }
      if (!s.start_time) {
        toast.error(t("clientPortal:projectRequestNew.shiftStartInvalid", { n: i + 1 }));
        return;
      }
      if (!s.end_time) {
        toast.error(t("clientPortal:projectRequestNew.shiftEndInvalid", { n: i + 1 }));
        return;
      }
      if (diffHours(s.start_time, s.end_time) === null) {
        toast.error(t("clientPortal:projectRequestNew.shiftEndAfterStart", { n: i + 1 }));
        return;
      }
      if (Number(s.staff_required) < 1) {
        toast.error(t("clientPortal:projectRequestNew.shiftStaffMin", { n: i + 1 }));
        return;
      }
    }

    const token = getToken();
    if (!token) {
      toast.error(t("clientPortal:projectRequestNew.sessionExpired"));
      return;
    }

    setSubmitting(true);
    try {
      // EVENTSFLO-CLIENT-REQUEST-SUBMISSION-BODY-STREAM-READ-FIX-V1:
      // Normalize optional string fields ("" → null) and trim required-ish
      // ones. Mirrors the fix applied in EF-CREW-ADMIN-UI-REGRESSION-AND-
      // FETCH-FIX-V1 (iteration_114) where empty strings on the Pydantic
      // payload triggered the same surface symptom.
      const cleanStr = (v) => {
        if (v === undefined || v === null) return null;
        if (typeof v === "string") {
          const s = v.trim();
          return s === "" ? null : s;
        }
        return v;
      };

      const baseShifts = normalised.map((s) => {
        const {
          _venue_manual,
          _loc_manual,
          _start_error,
          _end_error,
          _duration_error,
          _duration_value,
          duration_hours_str,
          ...clean
        } = s;
        return {
          ...clean,
          staff_required: Number(clean.staff_required),
          venue_id: clean.venue_id || null,
          venue_location_id: clean.venue_location_id || null,
          duration_hours: _duration_value,
          role: cleanStr(clean.role),
          activity: cleanStr(clean.activity),
          location: cleanStr(clean.location),
          notes: cleanStr(clean.notes),
        };
      });

      const payload = {
        ...form,
        project_name: form.project_name.trim(),
        event_type: cleanStr(form.event_type),
        venue: cleanStr(form.venue),
        venue_address: cleanStr(form.venue_address),
        meeting_point: cleanStr(form.meeting_point),
        start_date: cleanStr(form.start_date),
        end_date: cleanStr(form.end_date),
        notes: cleanStr(form.notes),
        requested_venue_id: form.requested_venue_id || null,
        requested_venue_location_id: form.requested_venue_location_id || null,
        shifts: baseShifts,
      };

      // Strip empty on_site_contact (server treats null as no contact)
      const osc = payload.on_site_contact;
      if (!osc || (!osc.name && !osc.phone && !osc.email)) {
        payload.on_site_contact = null;
      } else {
        payload.on_site_contact = {
          // EVENTSFLO-CLIENT-PORTAL-REQUEST-JOB-UX-COMPACT-SHIFT-DUPLICATE-CONTACTS-V1
          // Forward `client_contact_id` when the user picked a saved
          // contact so the backend can validate ownership and store
          // the FK alongside the snapshot. Skip the field when the
          // user is typing manually (avoids a null that the server
          // would have to special-case).
          ...(osc.client_contact_id
            ? { client_contact_id: osc.client_contact_id }
            : {}),
          name: cleanStr(osc.name),
          phone: cleanStr(osc.phone),
          email: cleanStr(osc.email),
        };
      }

      const r = await fetch(
        isEdit
          ? `${API_URL}/api/client/project-requests/${editRequestId}`
          : `${API_URL}/api/client/project-requests`,
        {
          method: isEdit ? "PATCH" : "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        },
      );

      // EVENTSFLO-CLIENT-REQUEST-SUBMISSION-BODY-STREAM-READ-FIX-V1:
      // Read the body *exactly once* via `.text()` and then attempt a
      // safe JSON.parse. This makes "body stream already read" structurally
      // impossible — the underlying Response stream is consumed in a single
      // call and we never call `.json()` on the response object directly.
      const rawText = await r.text();
      let data = null;
      if (rawText) {
        try {
          data = JSON.parse(rawText);
        } catch {
          // Non-JSON body (e.g. proxy HTML on 502). Keep raw for diagnostics.
          data = null;
        }
      }

      if (!r.ok) {
        // FastAPI returns `detail` either as a string OR as an array of
        // validation errors `[{loc, msg, type}]`. Render either cleanly.
        let message = `Failed to submit request (HTTP ${r.status})`;
        if (data && typeof data.detail === "string") {
          message = data.detail;
        } else if (data && Array.isArray(data.detail) && data.detail.length) {
          message = data.detail
            .map((d) => (typeof d === "string" ? d : d?.msg || JSON.stringify(d)))
            .join("; ");
        } else if (rawText && !data) {
          // Plain-text / HTML error body — keep first ~200 chars only.
          message = `${message}: ${rawText.slice(0, 200)}`;
        }
        throw new Error(message);
      }

      toast.success(
        isEdit
          ? "Request updated. Our team has been notified."
          : "Request submitted! Our team will review and get back to you.",
      );
      navigate("/client/project-requests");
    } catch (err) {
      console.error("Submit request error:", err);
      toast.error(err.message || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  // EVENTSFLO-CLIENT-PORTAL-PROJECT-REQUEST-EDIT-DELETE-V1
  // While the existing request is being fetched, render a small
  // loading state instead of the empty default form. Otherwise the
  // first paint shows defaults and the user (or e2e tests) may
  // observe an empty form before the hydration setState lands.
  if (isEdit && loadingExisting) {
    return (
      <div
        className="p-6 flex items-center gap-2 text-sm text-muted-foreground"
        data-testid="edit-loading"
      >
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading request…
      </div>
    );
  }

  return (
    <div className="space-y-3 max-w-5xl">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/client/project-requests" data-testid="back-to-requests-btn">
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to requests
          </Link>
        </Button>
        {/* EVENTSFLO-GUIDE-BUTTON-ROLLOUT-V1 */}
        <GuideButton testId="new-project-request-guide-btn" />
      </div>

      <div>
        <h1 className="text-xl font-bold" data-testid="new-request-title">
          {isEdit ? "Edit Project Request" : "New Project Request"}
        </h1>
        <p className="text-xs text-muted-foreground">
          {isEdit
            ? "Update your request below. Saving will notify our team to re-review."
            : "Submit a request for a new event. Our team will review and confirm pricing & availability before any booking is created."}
        </p>
      </div>

      <div className="p-2 bg-blue-500/10 border border-blue-500/20 rounded-md text-xs flex gap-2">
        <Info className="h-4 w-4 shrink-0 text-blue-600 mt-0.5" />
        <span className="text-blue-700">
          This is a <strong>request</strong>, not a confirmed booking. Crew
          allocation, pricing, and invoicing happen only after our team reviews
          and approves your request.
        </span>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Project Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <Label htmlFor="project_name">
                  Project / Event name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="project_name"
                  value={form.project_name}
                  onChange={(e) => setField("project_name", e.target.value)}
                  placeholder="e.g. Autumn Product Launch"
                  required
                  maxLength={200}
                  data-testid="input-project-name"
                />
              </div>
              <div>
                <Label htmlFor="event_type">Event Type</Label>
                <Input
                  id="event_type"
                  value={form.event_type}
                  onChange={(e) => setField("event_type", e.target.value)}
                  placeholder="Conference, Product Launch, Wedding…"
                  maxLength={100}
                  data-testid="input-event-type"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <Label htmlFor="start_date">{t("clientPortal:projectRequestNew.startDate")}</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setField("start_date", e.target.value)}
                  data-testid="input-start-date"
                />
              </div>
              <div>
                <Label htmlFor="end_date">{t("clientPortal:projectRequestNew.endDate")}</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setField("end_date", e.target.value)}
                  data-testid="input-end-date"
                />
              </div>
            </div>
            {/* EVENTSFLO-CLIENT-PORTAL-PROJECT-REQUEST-VENUE-LOCATION-DROPDOWNS-V1 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label htmlFor="venue_select">Venue</Label>
                {venues.length > 0 ? (
                  <>
                    <select
                      id="venue_select"
                      value={form.requested_venue_id || "__free_text__"}
                      onChange={(e) => {
                        // EVENTSFLO-CLIENT-PORTAL-FLEXIBLE-LOCATION-ENTRY-V1
                        // The "__free_text__" sentinel switches the form
                        // into typed-venue mode: requested_venue_id is
                        // cleared and the client types a fresh venue
                        // name in the Input below. Admin verifies +
                        // creates/links the permanent venue record.
                        if (e.target.value === "__free_text__") {
                          setForm((p) => ({
                            ...p,
                            requested_venue_id: "",
                            requested_venue_location_id: "",
                            venue: "",
                            venue_address: "",
                            meeting_point: "",
                          }));
                          setShifts((arr) =>
                            arr.map((s) =>
                              s._venue_manual
                                ? s
                                : { ...s, venue_id: "", venue_location_id: "" },
                            ),
                          );
                          return;
                        }
                        handleProjectVenueChange(e.target.value);
                      }}
                      className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                      data-testid="select-project-venue"
                    >
                      {venues.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.name}
                          {v.is_shared ? " (shared)" : ""}
                        </option>
                      ))}
                      <option value="__free_text__" data-testid="select-project-venue-option-free-text">
                        + Location not listed? Type it here
                      </option>
                    </select>
                    {!form.requested_venue_id && (
                      <>
                        <Input
                          id="venue"
                          value={form.venue}
                          onChange={(e) => setField("venue", e.target.value)}
                          placeholder="New location / venue name"
                          maxLength={300}
                          className="mt-2"
                          data-testid="input-venue-free-text"
                        />
                        <p
                          className="text-[11px] text-muted-foreground mt-1"
                          data-testid="venue-free-text-hint"
                        >
                          If the venue is not listed, enter the location name
                          and the EventsFlo team will verify it.
                        </p>
                      </>
                    )}
                  </>
                ) : (
                  <Input
                    id="venue"
                    value={form.venue}
                    onChange={(e) => setField("venue", e.target.value)}
                    placeholder={
                      venuesLoaded
                        ? "Venue name (no venues linked yet)"
                        : "Loading venues…"
                    }
                    maxLength={300}
                    data-testid="input-venue"
                  />
                )}
                {venuesLoaded && venues.length === 0 && !venuesError && (
                  <p className="text-[11px] text-muted-foreground mt-1" data-testid="no-venues-hint">
                    No venues are currently linked to your client profile.
                    You can still submit and our team will confirm the venue.
                  </p>
                )}
                {venuesError && (
                  <p className="text-[11px] text-amber-600 mt-1">
                    Could not load venues: {venuesError}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="venue_address">{t("clientPortal:projectRequestNew.venueAddress")}</Label>
                <Input
                  id="venue_address"
                  value={form.venue_address}
                  onChange={(e) => setField("venue_address", e.target.value)}
                  placeholder="Street, city, country"
                  maxLength={500}
                  data-testid="input-venue-address"
                />
              </div>
              <div>
                <Label htmlFor="meeting_point_select">
                  Location / Meeting point
                </Label>
                {locationsForVenue(form.requested_venue_id).length > 0 ? (
                  <select
                    id="meeting_point_select"
                    value={form.requested_venue_location_id}
                    onChange={(e) => handleProjectLocationChange(e.target.value)}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                    data-testid="select-project-location"
                  >
                    <option value="">(none)</option>
                    {locationsForVenue(form.requested_venue_id).map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <Input
                    id="meeting_point"
                    value={form.meeting_point}
                    onChange={(e) => setField("meeting_point", e.target.value)}
                    placeholder="Stage door, loading bay, reception…"
                    maxLength={300}
                    data-testid="input-meeting-point"
                  />
                )}
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground -mt-1">
              Default venue and location apply to new shifts. You can
              change them per shift if needed.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
            <CardTitle className="text-base">
              Shifts <span className="text-muted-foreground text-xs">({shifts.length})</span>
            </CardTitle>
            <div className="flex items-center gap-2">
              {/* EVENTSFLO-CLIENT-PORTAL-PROJECT-REQUEST-SHIFT-TIME-UX-V1 */}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleGenerateDailyShifts}
                disabled={!form.start_date || !form.end_date}
                title={
                  !form.start_date || !form.end_date
                    ? "Set Start date and End date to generate"
                    : "Generate one row per day in the date range using the first shift as template"
                }
                data-testid="generate-daily-shifts-btn"
              >
                <Calendar className="h-4 w-4 mr-1" />
                Generate daily shifts
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addShift}
                data-testid="add-shift-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add shift
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {shifts.map((s, i) => (
              <div
                key={i}
                className="p-2.5 border border-border/60 rounded-md space-y-2 relative"
                data-testid={`shift-row-${i}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-muted-foreground">
                    Shift {i + 1}
                  </span>
                  {shifts.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeShift(i)}
                      className="h-7 text-destructive hover:text-destructive"
                      data-testid={`remove-shift-${i}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                {/* EVENTSFLO-CLIENT-PORTAL-REQUEST-JOB-SHIFT-CARD-INLINE-COMPACT-V1
                    Apply-to-all chips. Shown only when ≥ 2 shifts exist.
                    Each chip copies one logical group of fields (times,
                    staff, role, activity) from this shift to every OTHER
                    shift, preserving each row's `date`. Empty source values
                    are silently ignored to prevent accidental wipes. */}
                {shifts.length > 1 && (
                  <div
                    className="flex flex-wrap items-center gap-1 -mt-1 mb-1"
                    data-testid={`shift-apply-toolbar-${i}`}
                  >
                    <span className="text-[11px] text-muted-foreground mr-1">
                      Apply to all:
                    </span>
                    <button
                      type="button"
                      onClick={() => applyTimesToAll(i)}
                      className="text-[11px] px-2 py-0.5 rounded-full border border-border/70 hover:bg-accent/60 transition-colors"
                      data-testid={`apply-times-to-all-${i}`}
                    >
                      Times
                    </button>
                    <button
                      type="button"
                      onClick={() => applyStaffToAll(i)}
                      className="text-[11px] px-2 py-0.5 rounded-full border border-border/70 hover:bg-accent/60 transition-colors"
                      data-testid={`apply-staff-to-all-${i}`}
                    >
                      Staff
                    </button>
                    <button
                      type="button"
                      onClick={() => applyRoleToAll(i)}
                      className="text-[11px] px-2 py-0.5 rounded-full border border-border/70 hover:bg-accent/60 transition-colors"
                      data-testid={`apply-role-to-all-${i}`}
                    >
                      Role
                    </button>
                    <button
                      type="button"
                      onClick={() => applyActivityToAll(i)}
                      className="text-[11px] px-2 py-0.5 rounded-full border border-border/70 hover:bg-accent/60 transition-colors"
                      data-testid={`apply-activity-to-all-${i}`}
                    >
                      Activity
                    </button>
                  </div>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                  <div>
                    <Label className="text-xs">
                      Date <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      type="date"
                      value={s.date}
                      onChange={(e) => updateShift(i, "date", e.target.value)}
                      required
                      data-testid={`shift-date-${i}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">
                      Start <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      placeholder="08:00"
                      value={s.start_time}
                      onChange={(e) =>
                        updateShift(i, "start_time", e.target.value)
                      }
                      onBlur={() => handleStartTimeBlur(i)}
                      aria-invalid={!!s._start_error}
                      data-testid={`shift-start-${i}`}
                    />
                    {s._start_error ? (
                      <p
                        className="text-[11px] text-red-600 mt-1"
                        data-testid={`shift-start-error-${i}`}
                      >
                        {s._start_error}
                      </p>
                    ) : null}
                  </div>
                  <div>
                    <Label className="text-xs">
                      End <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      placeholder="17:00"
                      value={s.end_time}
                      onChange={(e) =>
                        updateShift(i, "end_time", e.target.value)
                      }
                      onBlur={() => handleEndTimeBlur(i)}
                      aria-invalid={!!s._end_error}
                      data-testid={`shift-end-${i}`}
                    />
                    {s._end_error ? (
                      <p
                        className="text-[11px] text-red-600 mt-1"
                        data-testid={`shift-end-error-${i}`}
                      >
                        {s._end_error}
                      </p>
                    ) : null}
                  </div>
                  <div>
                    {/* EVENTSFLO-CLIENT-PORTAL-PROJECT-REQUEST-SHIFT-TIME-UX-V1 */}
                    <Label className="text-xs">Duration / hours</Label>
                    <Input
                      type="text"
                      inputMode="decimal"
                      placeholder="8 or 9.5"
                      value={s.duration_hours_str}
                      onChange={(e) =>
                        updateShift(i, "duration_hours_str", e.target.value)
                      }
                      onBlur={() => handleDurationBlur(i)}
                      aria-invalid={!!s._duration_error}
                      data-testid={`shift-duration-${i}`}
                    />
                    {s._duration_error ? (
                      <p
                        className="text-[11px] text-red-600 mt-1"
                        data-testid={`shift-duration-error-${i}`}
                      >
                        {s._duration_error}
                      </p>
                    ) : s.start_time && s.end_time && s.duration_hours_str ? (
                      <p
                        className="text-[11px] text-muted-foreground mt-1"
                        data-testid={`shift-duration-hint-${i}`}
                      >
                        {formatDurationHM(parseFloat(s.duration_hours_str))}
                      </p>
                    ) : null}
                  </div>
                  <div>
                    <Label className="text-xs">
                      Staff <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      type="number"
                      min={1}
                      max={200}
                      value={s.staff_required}
                      onChange={(e) =>
                        updateShift(i, "staff_required", e.target.value)
                      }
                      required
                      data-testid={`shift-staff-${i}`}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div className="sm:col-span-1">
                    <Label className="text-xs">Role / Task</Label>
                    <Input
                      value={s.role}
                      onChange={(e) => updateShift(i, "role", e.target.value)}
                      placeholder="e.g. Stagehand, Rigger, Steward"
                      maxLength={200}
                      data-testid={`shift-role-${i}`}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs">Activity</Label>
                    <Input
                      value={s.activity}
                      onChange={(e) =>
                        updateShift(i, "activity", e.target.value)
                      }
                      placeholder="Load-in, Show, Load-out…"
                      maxLength={200}
                      data-testid={`shift-activity-${i}`}
                    />
                  </div>
                </div>
                {/* EVENTSFLO-CLIENT-PORTAL-PROJECT-REQUEST-VENUE-LOCATION-DROPDOWNS-V1 */}
                {venues.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Venue</Label>
                      <select
                        value={s.venue_id || ""}
                        onChange={(e) =>
                          handleShiftVenueChange(i, e.target.value)
                        }
                        className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                        data-testid={`shift-venue-${i}`}
                      >
                        <option value="">(use project default)</option>
                        {venues.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.name}
                            {v.is_shared ? " (shared)" : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">Location / Meeting point</Label>
                      <select
                        value={s.venue_location_id || ""}
                        onChange={(e) =>
                          handleShiftLocationChange(i, e.target.value)
                        }
                        className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                        data-testid={`shift-location-${i}`}
                        disabled={!s.venue_id}
                      >
                        <option value="">(none)</option>
                        {locationsForVenue(s.venue_id).map((l) => (
                          <option key={l.id} value={l.id}>
                            {l.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
                <div>
                  <Label className="text-xs">Notes</Label>
                  <Textarea
                    value={s.notes}
                    onChange={(e) => updateShift(i, "notes", e.target.value)}
                    placeholder="Anything our team should know about this shift"
                    rows={2}
                    maxLength={2000}
                    data-testid={`shift-notes-${i}`}
                  />
                </div>
                {/* EVENTSFLO-CLIENT-PORTAL-SHIFT-REQUIREMENTS-LINE-ITEMS-V1 */}
                <ShiftRequirementsEditor
                  shiftIndex={i}
                  value={s.requirements || []}
                  onChange={(next) => updateShift(i, "requirements", next)}
                />
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">On-site Contact</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {/* EVENTSFLO-CLIENT-PORTAL-REQUEST-JOB-UX-COMPACT-SHIFT-DUPLICATE-CONTACTS-V1 */}
            {contactsLoaded && savedContacts.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="text-muted-foreground">Source:</span>
                <button
                  type="button"
                  className={`px-2 py-1 rounded-md border text-xs transition-colors ${
                    oscMode === "saved"
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border hover:bg-accent"
                  }`}
                  onClick={() => setOscMode("saved")}
                  data-testid="osc-mode-saved"
                >
                  Select from saved contacts
                </button>
                <button
                  type="button"
                  className={`px-2 py-1 rounded-md border text-xs transition-colors ${
                    oscMode === "manual"
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border hover:bg-accent"
                  }`}
                  onClick={() => {
                    setOscMode("manual");
                    // Clear FK so manual edits aren't tied to a saved contact.
                    handleSavedContactPick("");
                  }}
                  data-testid="osc-mode-manual"
                >
                  Enter manually
                </button>
              </div>
            )}
            {contactsLoaded && savedContacts.length === 0 && (
              <p className="text-[11px] text-muted-foreground" data-testid="osc-no-saved-hint">
                No saved contacts yet — enter the on-site contact below.
              </p>
            )}
            {oscMode === "saved" && savedContacts.length > 0 && (
              <div>
                <Label htmlFor="osc-saved-select" className="text-xs">
                  Saved contact
                </Label>
                <select
                  id="osc-saved-select"
                  value={oscSelectedId}
                  onChange={(e) => handleSavedContactPick(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                  data-testid="osc-saved-select"
                >
                  <option value="">— Select a contact —</option>
                  {savedContacts.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                      {c.role ? ` — ${c.role}` : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <Input
                placeholder="Name"
                value={form.on_site_contact.name}
                onChange={(e) => {
                  setOsc("name", e.target.value);
                  // Typing in a manual field implicitly switches to
                  // manual mode and drops any saved-contact FK so the
                  // backend isn't asked to validate ownership of a
                  // contact whose name has been edited.
                  if (form.on_site_contact.client_contact_id) {
                    handleSavedContactPick("");
                    setOscMode("manual");
                  }
                }}
                maxLength={200}
                data-testid="input-osc-name"
              />
              <Input
                placeholder="Phone"
                value={form.on_site_contact.phone}
                onChange={(e) => {
                  setOsc("phone", e.target.value);
                  if (form.on_site_contact.client_contact_id) {
                    handleSavedContactPick("");
                    setOscMode("manual");
                  }
                }}
                maxLength={50}
                data-testid="input-osc-phone"
              />
              <Input
                placeholder="Email"
                type="email"
                value={form.on_site_contact.email}
                onChange={(e) => {
                  setOsc("email", e.target.value);
                  if (form.on_site_contact.client_contact_id) {
                    handleSavedContactPick("");
                    setOscMode("manual");
                  }
                }}
                maxLength={200}
                data-testid="input-osc-email"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Optional — leave blank if the requester is the on-site contact.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Additional Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={form.notes}
              onChange={(e) => setField("notes", e.target.value)}
              placeholder="Budget hints, dress code, special requirements, anything else our team should know."
              rows={4}
              maxLength={4000}
              data-testid="input-notes"
            />
          </CardContent>
        </Card>

        <Separator />

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/client/project-requests")}
            disabled={submitting}
            data-testid="cancel-request-btn"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={submitting || loadingExisting}
            data-testid="submit-request-btn"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            {isEdit ? "Save changes" : "Submit Request"}
          </Button>
        </div>
      </form>
    </div>
  );
}
